import React, { useState, useEffect } from 'react';
import styles from './Equities.module.css';
import CreateForm from '../../Design/Forms/CreateForm';
import { FormFields } from '../../Design/Forms/FormJson';
import InvisibleButton from '../../Design/Button/Invisible/Invisible';
import {
  createSecurityClass,
  createSecurityGrants,
  fetchSecurityClasses,
  fetchShareholders
} from '../../../utils/securities.controller';
import Certificate from '../Certificate/Certificate';
import DataBox from '../../Design/DataBox/DataBox';
import { generateShareName } from '../../../utils/shareNameGenerator';

function Equities() {
  const [classFormData, setClassFormData] = useState({});
  const [grantFormData, setGrantFormData] = useState({});
  const [showClassForm, setShowClassForm] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [securityClasses, setSecurityClasses] = useState([]);
  const [equityGrantFields, setEquityGrantFields] = useState([...FormFields.equityGrant]);
  const [showCertificate, setShowCertificate] = useState(false);
  const [shareholders, setShareholders] = useState([]);
  const [selectedShareholder, setSelectedShareholder] = useState(null);
  const [previewShareName, setPreviewShareName] = useState('');


  const shareholderColumns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Shareholder', accessor: 'shareholder' },
    { header: 'Shares Issued', accessor: 'shares_issued',  format: 'number' },
    { header: 'Price per Share', accessor: 'price_per_share', format: 'currency-small'  },
    { header: 'Capital Committed', accessor: 'capital_committed', format: 'currency-large' },
    { header: 'Class Name', accessor: 'share_name' },
    { header: 'Class ID', accessor: 'security_class_id' },
    { header: 'Date Issued', accessor: 'created_at', format: 'date' }
  ];
  

  useEffect(() => {
    if (grantFormData.shareName) {
      const preview = generateShareName(grantFormData.shareName, shareholders);
      setPreviewShareName(preview);
    }
  }, [grantFormData.shareName, shareholders]);
  

  useEffect(() => {
    const loadShareholders = async () => {
      try {
        const data = await fetchShareholders();
        setShareholders(data || []);
      } catch (err) {
        console.error('Unexpected error fetching shareholders:', err);
      }
    };
    loadShareholders();
  }, []);
  
  // Fetch security classes on mount
  useEffect(() => {
    const fetchInitialClasses = async () => {
      try {
        const classes = await fetchSecurityClasses();
        setSecurityClasses(classes || []);
      } catch (error) {
        console.error('Initial fetch error:', error);
      }
    };
    fetchInitialClasses();
  }, []);

  // Fetch and update class list when grant form is shown
  useEffect(() => {
    const getSecurityClasses = async () => {
      try {
        const classes = await fetchSecurityClasses();
        console.log("Fetched security classes:", classes);
        setSecurityClasses(classes || []);

        if (classes && classes.length > 0) {
          const updatedFields = JSON.parse(JSON.stringify(FormFields.equityGrant));
          if (updatedFields[4] && updatedFields[4].name === "shareName") {
            updatedFields[4].options = classes.map(cls => cls.security_class_label);
          }
          setEquityGrantFields(updatedFields);
        }
      } catch (error) {
        console.error('Error fetching security classes:', error);
      }
    };
    if (showGrantForm) {
      getSecurityClasses();
    }
  }, [showGrantForm]);

  const handleFormToggle = (formType) => {
    if (formType === 'class') {
      setShowClassForm(!showClassForm);
      if (!showClassForm) setClassFormData({});
    } else if (formType === 'grant') {
      setShowGrantForm(!showGrantForm);
      if (!showGrantForm) setGrantFormData({});
    }
  };

  const handleClassFormSubmit = async (e) => {
    e.preventDefault();
    if (!classFormData.className || !classFormData.sharesReserved) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const sharesReserved = parseInt(classFormData.sharesReserved, 10);
      if (isNaN(sharesReserved)) {
        alert('Shares reserved must be a valid number');
        return;
      }

      const securityClassData = {
        security_category: 'equity',
        security_code: 10,
        security_type: classFormData.classType || 'common',
        shares_reserved: sharesReserved,
        security_class_label: classFormData.className,
      };

      const { error } = await createSecurityClass(securityClassData);

      if (error) {
        alert(`Failed to create security class: ${error.message}`);
      } else {
        alert('Security class created successfully!');
        setClassFormData({});
        setShowClassForm(false);

        const refreshedClasses = await fetchSecurityClasses();
        setSecurityClasses(refreshedClasses || []);
      }
    } catch (error) {
      alert(`Unexpected error: ${error.message}`);
    }
  };

  const handleGrantFormSubmit = async (e) => {
    e.preventDefault();

    if (
      !grantFormData.shareholder ||
      !grantFormData.sharesIssued ||
      !grantFormData.pricePerShare ||
      !grantFormData.capitalCommitted ||
      !grantFormData.shareName ||
      !grantFormData.vestingSchedule
    ) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const selectedClass = securityClasses.find(
        cls => cls.security_class_label === grantFormData.shareName
      );

      if (!selectedClass) {
        alert('Please select a valid security class');
        return;
      }

      const sharesIssued = parseInt(grantFormData.sharesIssued, 10);
      const pricePerShare = parseFloat(grantFormData.pricePerShare);
      const capitalCommitted = parseFloat(grantFormData.capitalCommitted);

      if (isNaN(sharesIssued) || isNaN(pricePerShare) || isNaN(capitalCommitted)) {
        alert('Please enter valid numbers for shares and monetary values');
        return;
      }

      const issuanceDate = grantFormData.issuanceDate || new Date().toISOString().split('T')[0];
      const generatedShareName = generateShareName(grantFormData.shareName, shareholders);


      const securityGrantData = {
        shareholder: grantFormData.shareholder,
        shares_issued: sharesIssued,
        price_per_share: pricePerShare,
        capital_committed: capitalCommitted,
        created_at: issuanceDate,
        share_name: generatedShareName,
        vesting_schedule: grantFormData.vestingSchedule === 'Yes',
        security_class_id: selectedClass.id
      };

      const { error } = await createSecurityGrants(securityGrantData);

      if (error) {
        alert(`Failed to create security grant: ${error.message}`);
      } else {
        alert('Security grant created successfully!');
        setGrantFormData({});
        setShowGrantForm(false);
      }
    } catch (error) {
      alert(`Unexpected error: ${error.message}`);
    }
  };

 
  return (
    <div className={styles.container}>
      <div className={styles.buttonContainer}>
        {/* Group 1: Class */}
        <div className={styles.buttonWithForm}>
          <InvisibleButton onClick={() => handleFormToggle('class')}>
            Create New Class <span className={styles.plusSign}>+</span>
          </InvisibleButton>
          {showClassForm && (
            <div className={styles.formsContainer}>
              <CreateForm
                formData={classFormData}
                setFormData={setClassFormData}
                onSubmit={handleClassFormSubmit}
                text="Create Class"
                fields={FormFields.equityClass}
              />
            </div>
          )}
        </div>

        {/* Group 2: Grant */}
        <div className={styles.buttonWithForm}>
          <InvisibleButton onClick={() => handleFormToggle('grant')}>
            Issue New Grants
          </InvisibleButton>
          {showGrantForm && (
  <div className={styles.formsContainer}>
    {previewShareName && (
      <div style={{ marginBottom: '0.75rem', fontStyle: 'italic', color: '#888' }}>
        Preview Share Name: <strong>{previewShareName}</strong>
      </div>
    )}
      <CreateForm
        formData={grantFormData}
        setFormData={setGrantFormData}
        onSubmit={handleGrantFormSubmit}
        text="Grant Equity"
        fields={equityGrantFields}
      />
     </div>
    )}
        </div>
      </div>

      {securityClasses.length > 0 && (
      <div className={styles.dataBoxWrapper}>
        <DataBox
          title="Current Security Classes"
          description="These are the equity classes you’ve created."
          data={securityClasses}
          columns={[
            { header: 'ID', accessor: 'id',  format: 'number' },
            { header: 'Type', accessor: 'security_type' },
            { header: 'Category', accessor: 'security_category' },
            { header: 'Code', accessor: 'security_code' },
            { header: 'Label', accessor: 'security_class_label' },
            { header: 'Shares Reserved', accessor: 'shares_reserved',  format: 'number' },
            { header: 'Created At', accessor: 'created_at', format: 'date' },
          ]}
        />
      </div>
      
      )}
          {shareholders.length > 0 && (
        <div className={styles.dataBoxWrapper}>
          <DataBox
            title="Shareholders"
            description="These are all the equity grants that have been issued."
            data={shareholders}
            columns={shareholderColumns}
            onRowAction={(shareholder) => {
              setSelectedShareholder(shareholder);
              setShowCertificate(true);
            }}
          />


        </div>
      )}

      {/* Toggle Certificate View */}
      <InvisibleButton onClick={() => setShowCertificate(!showCertificate)}>
        {showCertificate ? 'Hide Certificate' : 'Show Certificate'}
      </InvisibleButton>

      {showCertificate && selectedShareholder && <Certificate
        shareholder={selectedShareholder.shareholder}
        shareAmount={selectedShareholder.shares_issued}
        className={selectedShareholder.share_name}
        issuanceDate={selectedShareholder.created_at}
        pricePerShare={selectedShareholder.price_per_share}
        company="Miami Home Holdings Inc"
        />}
    </div>
  );
}

export default Equities;
